import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ title, subtitle, children }) => {
  const { isDark } = useTheme();
  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-8 ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-[#1d1d1f]'}`}>
      <div className="w-full max-w-md">
        <Link to="/login" className={`flex items-center gap-2 mb-6 text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
          <ArrowLeft size={18} /> Retour à la connexion
        </Link>
        <div className={`rounded-2xl border p-6 ${isDark ? 'bg-[#1A1A2E]/80 border-white/10' : 'bg-white border-gray-200 shadow-xl'}`}>
          <h1 className="text-2xl font-semibold mb-2">{title}</h1>
          <p className={`text-sm leading-6 mb-5 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
};

const submitClass = 'w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold py-5 rounded-xl';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(r.data.message);
    } catch (err) {
      setError(err.response?.status === 422 ? 'Adresse e-mail invalide' : 'Une erreur est survenue, réessaie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Mot de passe oublié" subtitle="Indique l'adresse de ton compte : tu recevras un lien pour choisir un nouveau mot de passe.">
      {message ? (
        <p className="text-sm leading-6" data-testid="forgot-done">{message}</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input type="email" required placeholder="ton@email.com" value={email}
            onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          {error && <p className="text-[#FF6B6B] text-sm">{error}</p>}
          <Button type="submit" disabled={loading} className={submitClass} data-testid="forgot-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Envoyer le lien'}
          </Button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError('8 caractères minimum.');
    if (password !== confirm) return setError('Les deux mots de passe ne sont pas identiques.');
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue, réessaie.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Shell title="Lien incomplet" subtitle="Ce lien de réinitialisation est incomplet.">
        <Link to="/mot-de-passe-oublie" className="text-[#81d8d0] text-sm font-medium">Refaire une demande</Link>
      </Shell>
    );
  }

  return (
    <Shell title="Nouveau mot de passe" subtitle="Choisis un nouveau mot de passe (8 caractères minimum).">
      {done ? (
        <p className="text-sm leading-6" data-testid="reset-done">Mot de passe modifié. Redirection vers la connexion…</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input type="password" required placeholder="Nouveau mot de passe" value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          <Input type="password" required placeholder="Confirmer le mot de passe" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
          {error && <p className="text-[#FF6B6B] text-sm" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={loading} className={submitClass} data-testid="reset-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
          </Button>
        </form>
      )}
    </Shell>
  );
};
